/**
 * CR001: Spend & Earn Offers Page
 * Displays spend-to-earn promotions (gift card/cash/points rewards)
 */

import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Gift, DollarSign, Coins, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useSpendEarnOffers } from "@/hooks/useSpendEarnOffers";
import { SpendEarnOfferCard } from "@/components/spend-earn/SpendEarnOfferCard";
import { 
  RewardForm, 
  computeOfferStatus,
  SpendEarnOffer 
} from "@/lib/types/spendEarnOffers";
import { getAllFeatureFlags, FLAGS } from "@/lib/featureFlags";

type StatusFilter = 'all' | 'active' | 'upcoming' | 'expired';
type RewardFilter = 'all' | RewardForm;

export default function SpendEarn() {
  const { isAdmin } = useAuth();
  const { offers, loading, error, refetch } = useSpendEarnOffers();
  
  // Check if UI is enabled
  const [uiEnabled, setUiEnabled] = useState<boolean | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [rewardFilter, setRewardFilter] = useState<RewardFilter>('all');
  
  useEffect(() => {
    getAllFeatureFlags().then(flags => {
      setUiEnabled(flags[FLAGS.SPEND_EARN_UI] ?? false);
    });
  }, []);
  
  // Filter offers
  const filteredOffers = useMemo(() => {
    return offers.filter(offer => {
      // Status filter
      const status = computeOfferStatus(offer);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      
      // Reward form filter
      if (rewardFilter !== 'all' && offer.reward_form !== rewardFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchFields = [
          offer.merchant_name,
          offer.headline_mechanic,
          offer.product_scope,
          offer.membership_required,
        ].filter(Boolean).map(s => s!.toLowerCase());
        
        if (!searchFields.some(field => field.includes(query))) return false;
      }
      
      return true;
    });
  }, [offers, statusFilter, rewardFilter, searchQuery]);
  
  // Count by reward type
  const rewardCounts = useMemo(() => {
    const counts = { all: 0, GIFT_CARD: 0, CASH: 0, POINTS: 0, OTHER: 0 };
    offers.forEach(offer => {
      const status = computeOfferStatus(offer);
      if (statusFilter === 'all' || status === statusFilter) {
        counts.all++;
        counts[offer.reward_form]++;
      }
    });
    return counts;
  }, [offers, statusFilter]);
  
  // If loading flag state
  if (uiEnabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  // If UI is disabled
  if (!uiEnabled && !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        <Gift className="h-16 w-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Coming Soon</h1>
        <p className="text-muted-foreground text-center max-w-md">
          Spend & Earn offers are coming soon. Check back later!
        </p>
        <Button asChild className="mt-6">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Gift Card Deals
          </Link>
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <header className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Gift Card Deals
            </Link>
          </Button>
          
          <div className="text-center">
            <div className="inline-block mb-4">
              <div className="w-16 h-1 bg-primary mx-auto mb-4" />
              <h1 className="text-3xl font-semibold tracking-tight text-primary">
                Spend & Earn Offers
              </h1>
              <div className="w-16 h-1 bg-primary mx-auto mt-4" />
            </div>
            <p className="text-muted-foreground text-lg mt-4">
              Earn rewards when you spend at participating retailers
            </p>
          </div>
          
          {!uiEnabled && isAdmin && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg text-center">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Admin Preview:</strong> Spend & Earn UI is currently disabled for public users.
              </p>
            </div>
          )}
        </header>
        
        {/* Filters */}
        <div className="mb-6 space-y-4">
          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search merchants or offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Filter chips */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* Status filter */}
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Offers</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Reward type filter */}
            <div className="flex gap-2">
              <Badge
                variant={rewardFilter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setRewardFilter('all')}
              >
                All ({rewardCounts.all})
              </Badge>
              <Badge
                variant={rewardFilter === 'GIFT_CARD' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setRewardFilter('GIFT_CARD')}
              >
                <Gift className="h-3 w-3 mr-1" />
                Gift Card ({rewardCounts.GIFT_CARD})
              </Badge>
              <Badge
                variant={rewardFilter === 'CASH' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setRewardFilter('CASH')}
              >
                <DollarSign className="h-3 w-3 mr-1" />
                Cash ({rewardCounts.CASH})
              </Badge>
              <Badge
                variant={rewardFilter === 'POINTS' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setRewardFilter('POINTS')}
              >
                <Coins className="h-3 w-3 mr-1" />
                Points ({rewardCounts.POINTS})
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={refetch}>Retry</Button>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {offers.length === 0 
                ? "No Spend & Earn offers available yet." 
                : "No offers match your filters."}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredOffers.map(offer => (
              <SpendEarnOfferCard
                key={offer.id}
                offer={offer}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
        
        {/* Results count */}
        {!loading && filteredOffers.length > 0 && (
          <p className="text-center text-sm text-muted-foreground mt-8">
            Showing {filteredOffers.length} of {offers.length} offers
          </p>
        )}
      </div>
    </div>
  );
}
